/**
 * sanitize.ts
 * 
 * This utility provides HTML sanitization functions to help prevent
 * Cross-Site Scripting (XSS) attacks when displaying user-generated content.
 */

/**
 * Sanitizes HTML strings to prevent XSS attacks
 * Allows a limited set of safe HTML tags and attributes
 * 
 * @param html - The HTML string to sanitize
 * @returns - Sanitized HTML string with potentially harmful content removed
 */
export const sanitizeHtml = (html: string): string => {
  if (!html) return '';
  
  // Define allowed tags and attributes
  const allowedTags = ['p', 'br', 'b', 'i', 'em', 'strong', 'u', 'ul', 'ol', 'li', 'span'];
  const allowedAttributes: Record<string, string[]> = {
    'span': ['style'],
    '*': ['class']
  };
  
  // Allowed style properties for span elements
  const allowedStyles = ['color', 'font-weight', 'text-decoration'];
  
  // Helper to check URL protocols
  const isSafeUrl = (url: string): boolean => {
    if (!url) return false;
    
    // Check for javascript: or data: URLs which could be used for XSS
    if (/^(javascript|data|vbscript|file):/i.test(url)) {
      return false;
    }
    
    return true;
  };
  
  // Custom implementation of HTML sanitization
  const sanitize = (input: string): string => {
    // Remove potentially harmful tags
    let sanitized = input
      // Remove script tags and their content
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      // Remove iframe tags
      .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
      // Remove object tags
      .replace(/<object\b[^<]*(?:(?!<\/object>)<[^<]*)*<\/object>/gi, '')
      // Remove embed tags
      .replace(/<embed\b[^<]*(?:(?!<\/embed>)<[^<]*)*<\/embed>/gi, '')
      // Remove on* event handlers
      .replace(/\s+on\w+\s*=\s*["']?[^"'>]*["']?/gi, '')
      // Remove javascript: URLs
      .replace(/\bhref\s*=\s*["']?javascript:[^"'>]*["']?/gi, '');
    
    // Process allowed tags
    const tempElement = document.createElement('div');
    tempElement.innerHTML = sanitized;
    
    // Helper function to process nodes recursively
    const processTags = (parent: Element) => {
      // Convert to array to avoid live collection issues during removal
      const children = Array.from(parent.children);
      
      for (const child of children) {
        const tagName = child.tagName.toLowerCase();
        
        // Remove disallowed tags
        if (!allowedTags.includes(tagName)) {
          // Keep text content but remove the element
          parent.replaceChild(document.createTextNode(child.textContent || ''), child);
          continue;
        }
        
        // Handle attributes
        const attributes = Array.from(child.attributes);
        for (const attr of attributes) {
          const attrName = attr.name.toLowerCase();
          
          // Keep only allowed attributes
          const tagAllowedAttrs = allowedAttributes[tagName] || [];
          const globalAllowedAttrs = allowedAttributes['*'] || [];
          
          if (![...tagAllowedAttrs, ...globalAllowedAttrs].includes(attrName)) {
            child.removeAttribute(attrName);
            continue;
          }
          
          // Special handling for style attribute (only on spans)
          if (attrName === 'style' && tagName === 'span') {
            const styles = attr.value.split(';');
            const safeStyles = styles.filter(style => {
              const [property] = style.split(':');
              const trimmedProp = property.trim().toLowerCase();
              return allowedStyles.some(allowed => trimmedProp === allowed);
            });
            
            if (safeStyles.length === 0) {
              child.removeAttribute('style');
            } else {
              child.setAttribute('style', safeStyles.join(';'));
            }
          }
          
          // Check URL attributes
          if (attrName === 'href' || attrName === 'src') {
            if (!isSafeUrl(attr.value)) {
              child.removeAttribute(attrName);
            }
          }
        }
        
        // Process children recursively
        if (child.children.length > 0) {
          processTags(child);
        }
      }
    };
    
    // Start processing from root
    processTags(tempElement);
    
    return tempElement.innerHTML;
  };
  
  try {
    return sanitize(html);
  } catch (error) {
    console.error('Error sanitizing HTML:', error);
    // If sanitization fails, return text-only content
    return html.replace(/<[^>]*>/g, '');
  }
};

/**
 * Simple text encoder for cases where you want to fully escape HTML
 * and display it as plain text
 * 
 * @param text - The text to encode
 * @returns - Text with HTML special characters encoded
 */
export const encodeHtml = (text: string): string => {
  if (!text) return '';
  
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

/**
 * Sanitizes a URL to ensure it's safe to use in href/src attributes
 * 
 * @param url - The URL to sanitize
 * @returns - Safe URL or empty string if URL is potentially harmful
 */
export const sanitizeUrl = (url: string): string => {
  if (!url) return '';
  
  // Check for javascript: or data: URLs which could be used for XSS
  if (/^(javascript|data|vbscript|file):/i.test(url)) {
    return '';
  }
  
  // Only allow http and https protocols
  try {
    const urlObj = new URL(url);
    if (!['http:', 'https:'].includes(urlObj.protocol)) {
      return '';
    }
    return url;
  } catch (e) {
    // If URL parsing fails, check if it might be a relative URL
    if (url.startsWith('/') || url.startsWith('./') || url.startsWith('../')) {
      return url;
    }
    return '';
  }
};

/**
 * Sanitizes user input for use in JSON or as object keys
 * to protect against prototype pollution
 * 
 * @param input - The string to sanitize
 * @returns - Sanitized string safe for use in JS objects
 */
export const sanitizeObjectKey = (input: string): string => {
  if (!input) return '';
  
  // Remove or replace potentially harmful keys like __proto__, constructor, etc.
  const dangerousProps = ['__proto__', 'constructor', 'prototype'];
  
  if (dangerousProps.includes(input)) {
    return `safe_${input}`;
  }
  
  return input;
}; 